import { BasicDataParam, GraphDataParam } from '../types/enums/graphDataParam'
import { Profile } from '../types/profile'
import { GraphDatapoint } from '../types/graphSeriesConfig'
import { getDefaultPowerBase, getDefaultTempBase, isBtBasedProfile } from './profileEditorUtils'
import { baseDataToSortedPoints, sortPoints } from './profileEditorUtils'
import { ProfileType } from '../types/enums/profileType'

export enum GraphPointReducerAction {
  Add = 'add',
  Delete = 'delete',
  Update = 'update',
  BatchUpdate = 'batchUpdate',
  Undo = 'undo',
  Redo = 'redo',
}

interface InitialValues {
  tempPoints: Array<GraphDatapoint>
  rpmPoints: Array<GraphDatapoint>
  fanPoints: Array<GraphDatapoint>
  powerPoints: Array<GraphDatapoint>
  profileType: ProfileType
}

interface GraphPointsState {
  tempPoints: Array<GraphDatapoint>
  rpmPoints: Array<GraphDatapoint>
  fanPoints: Array<GraphDatapoint>
  powerPoints: Array<GraphDatapoint>
  minX: number // readonly, set only in initial state
  history: Array<GraphPointsAction>
  historyIndex: number
  initialValues: InitialValues
}

interface GraphPointsAction {
  type: GraphPointReducerAction
  param?: GraphDataParam
  index?: number
  newTime?: number
  newValue?: number
  batchUpdateArray?: Array<GraphDatapoint>
}

const getParamArrayName = (param: GraphDataParam): 'tempPoints' | 'rpmPoints' | 'fanPoints' | 'powerPoints' => {
  if (param === BasicDataParam.BT || param === BasicDataParam.ET || param === BasicDataParam.INLET) {
    return 'tempPoints'
  } else if (param === BasicDataParam.RPM) {
    return 'rpmPoints'
  } else if (param === BasicDataParam.FAN) {
    return 'fanPoints'
  } else if (param === BasicDataParam.POWER) {
    return 'powerPoints'
  }
  return 'tempPoints'
}

const addHistoryToState = (state: GraphPointsState, action: GraphPointsAction): GraphPointsState => {
  const newHistoryAfterAction = [...state.history.slice(0, state.historyIndex), action]
  return {
    ...state,
    history: newHistoryAfterAction,
    historyIndex: newHistoryAfterAction.length,
  }
}

export const createInitialState = (profile: Profile, minX: number): GraphPointsState => {
  const sortedInitialValues = {
    tempPoints: baseDataToSortedPoints(profile.temperature_bezier || getDefaultTempBase(profile.profile_type)),
    fanPoints: baseDataToSortedPoints(profile.fan_bezier || []),
    rpmPoints: baseDataToSortedPoints(profile.rpm_bezier || []),
    powerPoints: baseDataToSortedPoints(profile.power_bezier || getDefaultPowerBase(profile.profile_type)),
    profileType: profile.profile_type,
  }
  return {
    ...sortedInitialValues,
    initialValues: sortedInitialValues,
    minX: minX,
    history: [],
    historyIndex: 0,
  }
}

const movePointsAfterPointsChange = (newState: GraphPointsState): GraphPointsState => {
  // move second fan and rpm point if start point is moved, and all points with lower msec (except first) than this
  if (isBtBasedProfile(newState.initialValues.profileType)) {
    const updatePoints = (pointsArray: Array<GraphDatapoint>, minMsec: number): Array<GraphDatapoint> => {
      return pointsArray.map((x, index) => {
        return index === 1 || (x.msec < minMsec && index > 0) ? { msec: minMsec, dataValue: x.dataValue } : x
      })
    }
    const minMsec =
      newState.initialValues.profileType === ProfileType.BT_POWER
        ? newState.powerPoints[1].msec
        : newState.tempPoints[1].msec
    return {
      ...newState,
      rpmPoints: updatePoints(newState.rpmPoints, minMsec),
      fanPoints: updatePoints(newState.fanPoints, minMsec),
    }
  } else {
    return newState
  }
}

export const graphPointsReducer = (state: GraphPointsState, action: GraphPointsAction): GraphPointsState => {
  const paramArrayName = getParamArrayName(action.param || BasicDataParam.ET)
  switch (action.type) {
    case GraphPointReducerAction.Add:
      const newStateAfterAdd = {
        ...addHistoryToState(state, action),
        [paramArrayName]: sortPoints([
          ...state[paramArrayName],
          { msec: action.newTime || 0, dataValue: action.newValue || 0 },
        ]),
      }
      return movePointsAfterPointsChange(newStateAfterAdd)
    case GraphPointReducerAction.Delete:
      const newArray = state[paramArrayName].filter((_x, index) => index !== action.index)
      // if the first point is deleted, set time on the current first point to 0
      // (when we delete a point, the array is always sorted, and the first point is always at 0):
      if (newArray.length > 0 && newArray[0].msec !== state.minX) {
        newArray[0] = {
          ...newArray[0],
          msec: state.minX,
        }
      }
      const newStateAfterDelete = {
        ...addHistoryToState(state, action),
        [paramArrayName]: newArray,
      }
      return movePointsAfterPointsChange(newStateAfterDelete)
    case GraphPointReducerAction.Update:
      const newStateAfterUpdate = {
        ...addHistoryToState(state, action),
        [paramArrayName]: sortPoints(
          state[paramArrayName].map((x, index) => {
            return index === action.index ? { msec: action.newTime || 0, dataValue: action.newValue || 0 } : x
          })
        ),
      }
      return movePointsAfterPointsChange(newStateAfterUpdate)
    case GraphPointReducerAction.BatchUpdate:
      return {
        ...addHistoryToState(state, action),
        [paramArrayName]: sortPoints(action.batchUpdateArray || []),
      }
    case GraphPointReducerAction.Undo:
    case GraphPointReducerAction.Redo:
      // undo and redo only support add, delete and update actions.
      //
      // when doing undo and redo, calculate new state from the initial state. Run the reducer with the same
      // actions again up to given step. If user can redo and dispatches some other action than undo/redo,
      // the future actions from redo will be lost, and the current end of history is the state after the
      // new action
      const newHistoryIndex =
        action.type === GraphPointReducerAction.Undo ? state.historyIndex - 1 : state.historyIndex + 1
      let newUndoState: GraphPointsState = {
        ...state.initialValues,
        minX: state.minX,
        history: [],
        historyIndex: state.historyIndex,
        initialValues: state.initialValues,
      }
      state.history.slice(0, newHistoryIndex).forEach((historyAction: GraphPointsAction) => {
        newUndoState = { ...newUndoState, ...graphPointsReducer(newUndoState, historyAction) }
      })
      return {
        ...newUndoState,
        history: state.history,
        historyIndex: newHistoryIndex,
      }
  }
}
